'use client';

import { memo, type ReactNode, useEffect, useRef } from 'react';

import { type Clip, ClipType } from '@air/api/types';

import { type AssetInsertOptions } from '~/components/asset-detail/AssetDetailPanel';
import { ImageOptionsSection } from '~/components/asset-detail/ImageOptionsSection';
import { type VersionReference } from '~/components/asset-detail/VersionReferenceRow';
import { VideoOptionsSection } from '~/components/asset-detail/VideoOptionsSection';
import { type ButtonProps } from '~/components/Button';
import { useCdnLink } from '~/hooks/useCdnLink';
import { useImageOptions } from '~/hooks/useImageOptions';

export type AssetOptionsSectionProps = {
  activeClip: Clip;
  workspaceId: string;
  showVersionReference: boolean;
  initialAssetOptions?: Partial<AssetInsertOptions>;
  onOptionsChange: (options: AssetInsertOptions) => void;
  /** Current version reference, owned by the parent so the disable/force-latest decision and the toggle UI share one source of truth. */
  versionReference: VersionReference;
  /** Called when the user commits a version reference change (after the CDN modal if needed). */
  onVersionReferenceChange: (value: VersionReference) => void;
  /** Override the section header text inside the embed options block. */
  sectionLabel?: string;
  /** Button size for the Generate CDN modal CTAs. Forwarded to `useCdnLink`. */
  cdnLinkModalButtonSize?: ButtonProps['size'];
};

export const AssetOptionsSection = memo(
  ({
    activeClip,
    workspaceId,
    showVersionReference,
    initialAssetOptions,
    onOptionsChange,
    versionReference,
    onVersionReferenceChange,
    sectionLabel,
    cdnLinkModalButtonSize,
  }: AssetOptionsSectionProps) => {
    const {
      customHeight,
      customWidth,
      handleHeightChange,
      handleToggleAspectRatioLock,
      handleWidthChange,
      isAspectRatioLocked,
      resetDimensionsForClip,
    } = useImageOptions({ clip: activeClip, initialAssetOptions });

    const { activeCdnLink, handleVersionReferenceChange, isDynamicEnabled } = useCdnLink({
      assetId: activeClip.assetId,
      cdnLinkModalButtonSize,
      setVersionReference: onVersionReferenceChange,
      workspaceId,
    });

    const isFirstRender = useRef(true);

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      resetDimensionsForClip(activeClip);
    }, [resetDimensionsForClip, activeClip]);

    const isDynamic = versionReference === 'dynamic';
    const displayWidth = isDynamic ? (activeClip.darWidth ?? activeClip.width ?? customWidth) : customWidth;
    const displayHeight = isDynamic ? (activeClip.darHeight ?? activeClip.height ?? customHeight) : customHeight;

    useEffect(() => {
      onOptionsChange({
        dynamicUrl: isDynamic ? activeCdnLink?.url : undefined,
        height: displayHeight,
        versionReference,
        width: displayWidth,
      });
    }, [activeCdnLink?.url, displayHeight, displayWidth, isDynamic, onOptionsChange, versionReference]);

    const isImageAsset = activeClip.type === ClipType.photo;
    const isVideoAsset = activeClip.type === ClipType.video;

    let section: ReactNode = null;

    if (isImageAsset) {
      section = (
        <ImageOptionsSection
          height={displayHeight}
          isAspectRatioLocked={isAspectRatioLocked}
          isDynamicEnabled={isDynamicEnabled}
          isResizeDisabled={isDynamic}
          onHeightChange={handleHeightChange}
          onToggleAspectRatioLock={handleToggleAspectRatioLock}
          onVersionReferenceChange={handleVersionReferenceChange}
          onWidthChange={handleWidthChange}
          sectionLabel={sectionLabel}
          showVersionReference={showVersionReference}
          versionReference={versionReference}
          width={displayWidth}
        />
      );
    } else if (isVideoAsset) {
      section = (
        <VideoOptionsSection
          isDynamicEnabled={isDynamicEnabled}
          onVersionReferenceChange={handleVersionReferenceChange}
          sectionLabel={sectionLabel}
          showVersionReference={showVersionReference}
          versionReference={versionReference}
        />
      );
    }

    if (!section) {
      return null;
    }

    return <div className="shrink-0 border-neutral-4 border-t p-4">{section}</div>;
  },
);

AssetOptionsSection.displayName = 'AssetOptionsSection';
